<template>
  <div class="cohort-list-item" :class="{ disabled: value.archived }">
    <CohortVerifiedHeading :value="value.cohortVerified" v-if="value.cohortVerified" />
    <CohortVerificationPendingHeading v-if="value.cohortVerificationPending" />
    <CohortPrivateHeading v-if="value.isPrivate" />
    <CohortPublishHeading :value="value.clientsPublishedView" />

    <div class="hvh-flex space-between p-3">
      <div class="hvh-flex grow-1 mr-2">
        <div class="analysis-icon p-2 mr-1">
          <IconAnalysis />
        </div>
        <div class="grow-1 ml-2">
          <CohortListItemHeaderEdit :value="value" @input="$emit('input', $event)" />
        </div>
      </div>
      <div class="hvh-flex align-center font-12px ml-2">
        <div class="hvh-flex column align-end py-1">
          <span class="bold">{{ value.createdBy }}</span>
          <span class="no-wrap">Updated: {{ browserDateTime(value.lastUpdatedOn) }}</span>
          <span class="no-wrap">{{ value.patientDatasource }}</span>
        </div>
        <div class="splitter-vertical h-100" />
        <div class="hvh-flex py-1">
          <div class="hvh-flex column">
            <span class="patient-count bold">{{ customNumberFormat((value.cohortStatistic || {}).totalCount) }}</span>
            <span>Patients</span>
          </div>
          <div class="hvh-flex align-start ml-4">
            <PinIcon :value="value.isPinned" @input="$emit('pin-cohort', $event)" />
            <CohortListItemMenu
              :item="value"
              v-bind="options"
              :isEditableByUser="value.isEditableByUser"
              :selectedCohortStatus="value.creationStatus"
              :availableClients="availableClients"
              @hide-cohort="$emit('hide-cohort', !value.isPrivate)"
              @clinically-verify="$emit('clinically-verify', !value.cohortVerified)"
              @ask-clinically-verify="$emit('ask-clinically-verify')"
              @archive="$emit('archive')"
              @archive-all-versions="$emit('archive-all-versions')"
              @copy-url="$emit('copy-url')"
              @new-analysis="$emit('new-analysis')"
              @publish="publishCohort"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="details">
      <div class="splitter-horizontal p-3" v-if="showDetails">
        <div class="mb-3" v-if="inclusionCriteria">
          <div class="strong">Clinical Criteria</div>
          <div v-for="(group, groupInd) in inclusionCriteria.children" :key="groupInd">
            <div class="hvh-flex wrap align-center" v-for="({ item }, itemInd) in group.children" :key="`group${groupInd}-${itemInd}`">
              <div class="hvh-flex align-baseline ml-1 mr-2" v-if="item.criterionOperator">
                <v-icon color="primary" small>mdi-subdirectory-arrow-right</v-icon>
                <span class="font-12px">{{ item.criterionOperator.toUpperCase() }}</span>
              </div>
              <CohortListItemPill :item="value" @click="copyItem(value)" v-for="(value, ind) in item.value" :key="`group${groupInd}-${itemInd}-${ind}`" />
              <span class="font-sm mb-1">{{ [item.occurances, item.separated, item.then].filter((f) => f).join(' ') }}</span>
            </div>
          </div>
        </div>
        <div class="mb-3" v-if="timing.length">
          <div class="strong">Timing</div>
          <div v-for="(item, ind) in timing" :key="ind">{{ item }}</div>
        </div>
        <div class="mb-3" v-if="demographics.length">
          <div class="strong mb-1">Demographic Restrictions</div>
          <div class="hvh-flex wrap">
            <DPDPill class="pill demographic" :label="item.label" v-for="(item, ind) in demographics" :key="ind" />
          </div>
        </div>
        <div class="position-relative">
          <div class="strong">Analytics</div>
          <div class="flex wrap" v-if="analytics">
            <a class="mr-3" :href="`/Analytics/CohortAnalyticRun/${item.items.first().id}/Result`" v-for="item in groupedAnalytics" :key="item.key">{{ analyticLabel(item) }}</a>
            <span v-if="!analytics.any()">-</span>
          </div>
          <Loader v-else />
        </div>
      </div>
      <div class="hvh-flex center align-center button clickable" @click="toggleShowDetails">
        DETAILS
        <v-icon v-if="!showDetails">mdi-menu-down</v-icon>
        <v-icon v-else>mdi-menu-up</v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import eqnode from 'eqnode';
import axios from '../../../axios';
import { getDemographics, regionalizeStates } from '../../../libraries/cohorts';
import { customNumberFormat, errorDebug, tryParse } from '../../../libraries/common'
import CohortListItemPill from './CohortListItemPill.vue';
import CohortListItemMenu from './CohortListItemMenu.vue';
import IconAnalysis from '../../common/icon/IconAnalysis.vue';
import DPDPill from './DPDPill.vue';
import PinIcon from './icons/PinIcon.vue';
import CohortVerifiedHeading from './CohortVerifiedHeading.vue';
import CohortPrivateHeading from './CohortPrivateHeading.vue';
import CohortListItemHeaderEdit from './headerEdit/CohortListItemHeaderEdit.vue';
import { getCriteriaCode } from '../../../libraries/criteria';
import CohortPublishHeading from './CohortPublishHeading.vue';
import CohortVerificationPendingHeading from './CohortVerificationPendingHeading.vue';
export default {
  components: { CohortListItemPill, CohortListItemMenu, IconAnalysis, DPDPill, PinIcon, CohortVerifiedHeading, CohortPrivateHeading, CohortListItemHeaderEdit, CohortPublishHeading, CohortVerificationPendingHeading },
  props: ['value', 'options', 'availableClients'],
  data() {
    return {
      showDetails: false,
      analytics: null,
      isLoading: false,
    };
  },
  computed: {
    isCreatorCheck() {
      return this.value.createdBy === this.$store.state.user.user.email;
    },
    cohortAnalytics() {
      return this.analytics?.groupBy(f => f.analytic.name).map(f => `${f.key} (${f.items.length})`).join(', ') || '-';
    },
    inclusionCriteria() {
      var node = eqnode.fromObject(tryParse(this.value.inclusionCriteria));
      var criterias = node.map(f => {
        if (f.item.selectedTreeElements) {
          var ind = f.getParent && f.getParent().children.indexOf(f);

          var then = '';
          if (['Then After', 'Then Before'].includes(this.value.criterionOperator)) {
            then = [
              this.value.criterionOperator.replace('Then ').toLowerCase(),
              this.value.thenAfterMonths > 0 && `${this.value.thenAfterMonths} month${this.value.thenAfterMonths > 1 ? 's' : ''}`,
              this.value.thenAfterDays > 0 && `${this.value.thenAfterDays} day${this.value.thenAfterDays > 1 ? 's' : ''}`,
            ].filter(f => f)
              .join(' ');
          }

          return {
            beforeIndexDate: f.item.beforeIndexDate,
            criterionOperator: !!ind && f.item.criterionOperator,
            excluded: f.item.excluded,
            occurances: `Occurring ${f.item.occurancesNum} time${f.item.occurancesNum > 1 ? 's' : ''}`,
            separated: f.item.occurancesNum > 1 && `separated by ${f.item.separatedByMonths} month${f.item.separatedByMonths > 1 ? 's' : ''}`,
            then,
            value: (f.item.selectedTreeElements || [])
              .map(g => (f.item.eventHashMap || {})[g] ? { ...f.item.eventHashMap[g], search: g } : { label: g, type: 'bad-code', search: `error-${g}` })
          };
        }
      });
      var hasValues = criterias.toArray().filter(f => f).any(f => f.value.any());
      return hasValues && criterias;
    },
    timing() {
      var advancedOpts = {
        timeBeforeCriteria: 'Enrollment time before criteria',
        timeAfterCriteria: 'Enrollment time after criteria',
        enrollmentTime: 'Minimum enrollment time',
      };

      return Object.keys(advancedOpts)
        .filter(f => this.value[f])
        .map(f => `${advancedOpts[f]}: ${this.value[f]}`);
    },
    demographics() {
      var staticDemographics = getDemographics();
      var demographicValues = [];
      if (staticDemographics.ageRange.join(' - ') !== [this.value.minAge, this.value.maxAge].join(' - ')) {
        demographicValues.push({ label: 'Age range: ' + [this.value.minAge, this.value.maxAge].join(' - ') });
        demographicValues.push({ label: this.value.isAgeAtIndexDate ? 'Age at Index Date' : 'Current Age' });
      }
      if (staticDemographics.years.join(' - ') !== [this.value.minYear, this.value.maxYear].join(' - '))
        demographicValues.push({ label: 'Year range: ' + [this.value.minYear, this.value.maxYear].join(' - ') });
      if ((staticDemographics.genders).sortBy(f => f).join(', ') !== this.value.gender.sortBy(f => f).join(', '))
        demographicValues.push({ label: 'Gender: ' + this.value.gender.join(', ') });
      if (this.value.insuranceTypes.any()
        && (this.options.availableInsuranceTypes || []).any()
        && this.options.availableInsuranceTypes.map(f => f.id).sortBy(f => f).join(', ') !== this.value.insuranceTypes.sortBy(f => f).join(', ')) {
        var insuranceTypes = this.value.insuranceTypes.map(f => (this.options.availableInsuranceTypes.find(g => g.id === f) || {}).name);
        demographicValues.push({ label: `Insurance types: ${insuranceTypes.join(', ')}` });
      }
      if (!staticDemographics.states.map(f => f.value).all(f => this.value.states.includes(f))) {
        var regionalizedStates = regionalizeStates(this.value.states);
        demographicValues = demographicValues.concat(regionalizedStates);
      }

      return demographicValues;
    },
    groupedAnalytics() {
      return (this.analytics || [])
        .sortBy(f => f.id)
        .reverse()
        .groupBy(f => f.analytic.analyticIdentifier);
    },
  },
  methods: {
    publishCohort(value) {
      this.$emit('publish', value);
    },
    toggleShowDetails() {
      this.showDetails = !this.showDetails;
      if (!this.isLoading && !this.analytics) {
        this.isLoading = true;
        axios.get(`/Analytics/Cohort/GetCohortRuns?cohortId=${this.value.id}`)
          .then(response => this.analytics = response.data)
          .catch(errorDebug)
          .then(() => this.isLoading = false);
      }
    },
    analyticLabel(item) {
      var analytic = item.items.first().analytic;
      return analytic.name + (item.items.length > 1 ? ` (${item.items.length})` : '');
    },
    copyItem(item) {
      localStorage.setItem('clipboard', JSON.stringify(item));
      navigator.clipboard.writeText(getCriteriaCode(item.label) || item.label)
        .then(() => this.$notify('Copied to clipboard!'))
    },
    customNumberFormat,
  },
}
</script>

<style lang="scss" scoped>
.cohort-list-item {
  border-radius: 5px;
  box-shadow: 0 1px 4px 1px rgba(6, 10, 36, 0.1), 0 5px 10px 0 rgba(6, 10, 36, 0.08);
  overflow: hidden;

  .analysis-icon {
    min-width: 46px;
    height: 46px;
    background-color: #d2ecf3;
    border-radius: 8px;
  }

  .splitter-vertical {
    border-left: 1px solid #d2d2d4;
    margin: 0 10px;
  }

  .details {
    .splitter-horizontal {
      border-top: 1px solid #d2d2d4;
    }
    .button {
      background-color: rgba(185, 192, 210, 0.14);
      color: #8c9093;
      font-weight: bold;
      letter-spacing: 0.3px;
    }
  }

  .patient-count {
    min-width: 67px;
  }
}
</style>